import { useEffect, useMemo, useState } from 'react';

import { useSaveSettings } from '@/api/queries';
import type { Setting, SettingsSection } from '@/api/types';
import { FormError } from '@/ui/StateCard';
import { buttonState } from '@/ui/ActionButton';
import { SaveBar } from '@/ui/SaveBar';
import { useSession } from '@/session/SessionContext';

type Values = Record<string, unknown>;

function valuesOf(section: SettingsSection): Values {
  return Object.fromEntries(section.settings.map((s) => [s.key, s.value]));
}

function SettingField({
  setting,
  value,
  onChange,
  disabled,
}: {
  setting: Setting;
  value: unknown;
  onChange: (value: unknown) => void;
  /** Read-only: the caller may view settings but not change them. */
  disabled: boolean;
}) {
  const id = `setting-${setting.key}`;
  const hintId = `${id}-hint`;
  switch (setting.type) {
    case 'boolean':
      return (
        <label className="check">
          <input id={id} type="checkbox" checked={Boolean(value)} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
          <span className="ck-body">
            {setting.label}
            <span className="hint">{setting.description}</span>
          </span>
        </label>
      );
    case 'select':
      return (
        <div className="field">
          <label htmlFor={id}>{setting.label}</label>
          <select id={id} value={String(value ?? '')} aria-describedby={hintId} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
            {setting.options.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
          <span id={hintId} className="hint">{setting.description}</span>
        </div>
      );
    case 'number':
      return (
        <div className="field">
          <label htmlFor={id}>{setting.label}</label>
          <input
            id={id}
            type="number"
            step="any"
            value={value === '' || value === null || value === undefined ? '' : String(value)}
            aria-describedby={hintId}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          />
          <span id={hintId} className="hint">{setting.description}</span>
        </div>
      );
    default:
      return (
        <div className="field">
          <label htmlFor={id}>{setting.label}</label>
          <input id={id} type="text" value={String(value ?? '')} aria-describedby={hintId} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
          <span id={hintId} className="hint">{setting.description}</span>
        </div>
      );
  }
}

export function SectionForm({ section }: { section: SettingsSection }) {
  const save = useSaveSettings();
  const editable = useSession().can('settings.manage');
  const stored = useMemo(() => valuesOf(section), [section]);
  const [values, setValues] = useState<Values>(stored);
  useEffect(() => setValues(stored), [stored]);

  const changed = section.settings.some((s) => values[s.key] !== stored[s.key]);

  return (
    <div className="card">
      <form
        className="card-body stack-md"
        onSubmit={(event) => {
          event.preventDefault();
          const diff = Object.fromEntries(section.settings.filter((s) => values[s.key] !== stored[s.key]).map((s) => [s.key, values[s.key]]));
          save.mutate(diff);
        }}
      >
        <p className="hint">{section.description}</p>
        <FormError error={save.error} />
        <div className="grid grid-3">
          {section.settings.map((setting) => (
            <SettingField
              key={setting.key}
              setting={setting}
              value={values[setting.key]}
              onChange={(v) => setValues((current) => ({ ...current, [setting.key]: v }))}
              disabled={!editable}
            />
          ))}
        </div>

        {editable ? (
          <SaveBar changed={changed} state={buttonState(save)} onReset={() => setValues(stored)} />
        ) : (
          <p className="hint">You can view these settings but not change them.</p>
        )}
      </form>
    </div>
  );
}
